/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { If, Then, Else } from "react-if";
import { FlexContainer, Text, theme, ExtendedTooltip } from "@nordcloud/gnui";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { TooltipText } from "~/components/Styled/TooltipText";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import {
  FlexColumnSmall,
  MediumColumn,
  MediumIconColumn,
  SmallColumn,
} from "~/components/Tables/styles";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { FutureActionForResourceWithId } from "../../types/ResourceDetailsActionsQuery";
import { ResourceDetailsActionsTableActions } from "./ResourceDetailsActionsTableActions";

const columnHelper = createColumnHelper<FutureActionForResourceWithId>();

export function columns() {
  return [
    columnHelper.accessor(() => {}, {
      header: "Resource Status",
      cell: () => {
        return (
          <FlexContainer justifyContent="center" alignItems="center">
            <ExtendedTooltip
              caption="Upcoming"
              hideTimeout={0}
              position="start"
            >
              <StyledSVGIcon
                name="waitingFilled"
                color={theme.color.support.grey}
              />
            </ExtendedTooltip>
          </FlexContainer>
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor("actionName", {
      id: "type",
      header: "Action Name/Type",
      cell: ({ row }) => {
        return (
          <FlexContainer direction="column" alignItems="start">
            <Text tag="div" color={theme.color.text.text01}>
              {row.original.actionName}
            </Text>
            <Text tag="div" size="sm" color={theme.color.text.text02}>
              {row.original.actionType}
            </Text>
          </FlexContainer>
        );
      },
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor("resourceGroupName", {
      header: "Resource Group Name",
      cell: ({ row }) => {
        return (
          <If
            condition={
              row.original.resourceGroupName && row.original.resourceGroupId
            }
          >
            <Then>
              <NameWithLink
                name={row.original.resourceGroupName ?? ""}
                link={ROUTES.resourceGroups.generateDetails(
                  row.original.resourceGroupId ?? ""
                )}
              />
            </Then>
            <Else>No Resource Group</Else>
          </If>
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor("eventName", {
      header: "Event Name",
      cell: ({ row }) => {
        return (
          <NameWithLink
            name={row.original.eventName}
            link={ROUTES.events.generateDetails(row.original.eventId)}
          />
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor("startTime", {
      header: "Start Time (local time)",
      cell: ({ row }) => {
        const startTimeTZ = dayjs(row.original.startTime)
          .tz(row.original.scheduleTimezone)
          .format(dateFormat.dayMonthYearHourMinute);

        return (
          <FlexContainer alignItems="center">
            <ExtendedTooltip
              caption={
                <>
                  Plan Time: {startTimeTZ}
                  <br />
                  Timezone: {row.original.scheduleTimezone}
                </>
              }
            >
              <TooltipText
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {dayjs(row.original.startTime).format(
                  dateFormat.dayMonthYearHourMinute
                )}
              </TooltipText>
            </ExtendedTooltip>
          </FlexContainer>
        );
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        return (
          <ResourceDetailsActionsTableActions
            isFutureAction
            planId={row.original.planId}
          />
        );
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}
